import { useEffect, useRef } from "react";

import { NetworkStatus, useQuery } from "@apollo/client";
import type { ApolloQueryResult } from "@apollo/client";
import uniqBy from "lodash/uniqBy";

import { GET_NODES } from "../../../common/operations/queries";
import { errorNotification } from "../../../common/variables/notification";
import { useSelectedLocationDropdown } from "../../../common/variables/selectedLocationDropdown";
import type { GetNodesQuery, GetNodesQueryVariables } from "./../../../API";
import { DEFAULT_LIMIT_COUNT } from "../../../common/variables/common";

interface GetNodesDataInterface {
  data?: GetNodesQuery;
  loading: boolean;
}

export const NODES_TABLE_LIMIT = 25;

export const useGetNodes = (): GetNodesDataInterface => {
  const locationVariable = useSelectedLocationDropdown();
  const locationId = locationVariable?.value;

  const { data, fetchMore, loading, networkStatus } = useQuery<
    GetNodesQuery,
    GetNodesQueryVariables
  >(GET_NODES, {
    variables: {
      limit: DEFAULT_LIMIT_COUNT,
      locationId: locationId ?? "",
    },
    skip: !locationId,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
  });

  const fetchAllRunIdRef = useRef(0);
  const isFetchingAllPagesRef = useRef(false);

  useEffect(() => {
    if (!locationId) return;

    if (loading) return;

    if (networkStatus !== NetworkStatus.ready) return;

    const initialNextToken = data?.getNodes?.nextToken;

    if (!initialNextToken) return;

    if (isFetchingAllPagesRef.current) return;

    const runId = ++fetchAllRunIdRef.current;

    let cancelled = false;

    isFetchingAllPagesRef.current = true;

    const fetchAllPages = async () => {
      let currentNextToken: string | null | undefined = initialNextToken;
      const seenTokens = new Set<string>();

      try {
        while (
          !cancelled &&
          runId === fetchAllRunIdRef.current &&
          currentNextToken
        ) {
          if (seenTokens.has(currentNextToken)) break;

          seenTokens.add(currentNextToken);

          const response: ApolloQueryResult<GetNodesQuery> = await fetchMore({
            variables: {
              limit: DEFAULT_LIMIT_COUNT,
              locationId,
              nextToken: currentNextToken,
            },
            updateQuery: (prev, { fetchMoreResult }) => {
              if (!fetchMoreResult) {
                return prev;
              }

              const uniqueNodes = uniqBy(
                [
                  ...(prev.getNodes?.items ?? []),
                  ...(fetchMoreResult.getNodes?.items ?? []),
                ],
                item => item?.id
              );

              return {
                getNodes: {
                  ...fetchMoreResult.getNodes,
                  items: uniqueNodes,
                },
              };
            },
          });

          currentNextToken = response.data?.getNodes?.nextToken ?? null;
        }
      } catch (error) {
        console.error("Error fetching additional node pages:", error);

        errorNotification();
      } finally {
        if (runId === fetchAllRunIdRef.current) {
          isFetchingAllPagesRef.current = false;
        }
      }
    };

    void fetchAllPages();

    return () => {
      cancelled = true;

      if (runId === fetchAllRunIdRef.current) {
        isFetchingAllPagesRef.current = false;
      }
    };
  }, [
    data?.getNodes?.nextToken,
    fetchMore,
    loading,
    locationId,
    networkStatus,
  ]);

  const nodesLoading =
    loading ||
    networkStatus === NetworkStatus.fetchMore ||
    networkStatus === NetworkStatus.refetch;

  return { data, loading: nodesLoading };
};
